// src/components/planner/modals/StudyTypeModal.tsx

import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';

const API_BASE_URL = '/api';

const OTHER_COLORS = [
    '#f59e0b', '#84cc16', '#06b6d4', '#8b5cf6',
    '#f43f5e', '#14b8a6', '#fb923c', '#a78bfa'
];

interface StudyTypeModalProps {
    sessionId: string;
    currentType: string;
    currentCustomType?: string;
    currentColor?: string;
    onClose: () => void;
    onUpdate: (sessionId: string, type: string, customType?: string, color?: string) => void;
}

const StudyTypeModal: React.FC<StudyTypeModalProps> = ({
    sessionId,
    currentType,
    currentCustomType,
    currentColor,
    onClose,
    onUpdate
}) => {
    const [type, setType] = useState(currentType || 'flashcards');
    const [customType, setCustomType] = useState(currentCustomType || '');
    const [selectedColor, setSelectedColor] = useState(currentColor || OTHER_COLORS[0]);
    const [savedCustomTypes, setSavedCustomTypes] = useState<{ name: string, color: string }[]>([]);
    const [selectedChip, setSelectedChip] = useState(currentCustomType || '');

    useEffect(() => {
        const load = async () => {
            try {
                const token = localStorage.getItem('accessToken');
                const res = await fetch(`${API_BASE_URL}/planner/custom-study-types`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setSavedCustomTypes(data.types || []);
                }
            } catch (e) { }
        };
        load();
    }, []);

    const handleChipSelect = (ct: { name: string, color: string }) => {
        setSelectedChip(ct.name);
        setCustomType(ct.name);
        setSelectedColor(ct.color);
    };

    const handleCustomTypeInput = (value: string) => {
        setCustomType(value);
        setSelectedChip(''); // deseleciona chip ao digitar manualmente
    };

    const handleSave = async () => {
        if (type === 'other' && !customType.trim() && !selectedChip) return;

        // Se é um tipo novo (não é um chip salvo), persiste no backend
        if (type === 'other' && customType.trim() && !selectedChip) {
            try {
                const token = localStorage.getItem('accessToken');
                await fetch(`${API_BASE_URL}/planner/custom-study-types`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ name: customType.trim(), color: selectedColor })
                });
            } catch (e) { }
        }

        onUpdate(
            sessionId,
            type,
            type === 'other' ? (customType.trim() || undefined) : undefined,
            type === 'other' ? selectedColor : undefined
        );
    };

    return (
        <div className="plnr-modal-overlay" onClick={onClose}>
            <div className="plnr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="plnr-modal-header">
                    <h3>Editar Tipo de Estudo</h3>
                    <button className="plnr-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="plnr-modal-body">
                    <div className="plnr-form-group">
                        <label>Tipo de estudo</label>
                        <select
                            value={type}
                            onChange={(e) => {
                                setType(e.target.value);
                                setCustomType('');
                                setSelectedChip('');
                            }}
                            className="plnr-input"
                        >
                            <option value="flashcards">Flashcards</option>
                            <option value="questions">Questões</option>
                            <option value="reading">Leitura</option>
                            <option value="review">Revisão</option>
                            <option value="mindmap">Mapa Mental</option>
                            <option value="notes">Resumos e Anotações</option>
                            <option value="class">Aula</option>
                            <option value="other">Outro</option>
                        </select>
                    </div>

                    {type === 'other' && (
                        <>
                            {/* CHIPS DE TIPOS JÁ SALVOS */}
                            {savedCustomTypes.length > 0 && (
                                <div className="plnr-form-group">
                                    <label>Tipos anteriores</label>
                                    <div className="plnr-custom-type-chips">
                                        {savedCustomTypes.map(ct => (
                                            <button
                                                key={ct.name}
                                                type="button"
                                                className={`plnr-custom-type-chip ${selectedChip === ct.name ? 'active' : ''}`}
                                                style={{
                                                    borderColor: ct.color,
                                                    backgroundColor: selectedChip === ct.name ? ct.color : 'transparent',
                                                    color: selectedChip === ct.name ? 'white' : ct.color
                                                }}
                                                onClick={() => handleChipSelect(ct)}
                                            >
                                                {ct.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* INPUT PARA TIPO NOVO */}
                            <div className="plnr-form-group">
                                <label>{savedCustomTypes.length > 0 ? 'Ou digite um novo tipo' : 'Nome do tipo de estudo'}</label>
                                <input
                                    type="text"
                                    value={customType}
                                    onChange={(e) => handleCustomTypeInput(e.target.value)}
                                    placeholder="Ex: Mapas Conceituais"
                                    className="plnr-input"
                                />
                            </div>

                            {/* CORES — só para tipo novo (sem chip selecionado) */}
                            {!selectedChip && (
                                <div className="plnr-form-group">
                                    <label>Cor</label>
                                    <div className="plnr-color-grid">
                                        {OTHER_COLORS.map(color => (
                                            <button
                                                key={color}
                                                type="button"
                                                className={`plnr-color-option ${selectedColor === color ? 'active' : ''}`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => setSelectedColor(color)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="plnr-modal-footer">
                    <button className="plnr-btn secondary" onClick={onClose}>
                        Cancelar
                    </button>
                    <button className="plnr-btn primary" onClick={handleSave}>
                        <Save size={16} />
                        Salvar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StudyTypeModal;